import { Divider, Form, Button, DatePicker, InputNumber, Table, message, Space, Upload, UploadProps } from "antd";
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import { useState, useEffect } from "react";
import { Dispatch, SetStateAction } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";
import { localUploadBaseProps } from "../../../utils/localUploadRequest";

import "./InternetLeadsConversion.scss";
import { createColumns } from "./content/InternetLeadsColumns";
import { fetchInternetLeadsData } from "./utils/fetchInternetLeadsData";
import { Colors } from "../../../constants/Colors";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import ContentTitle from "../../../components/ui/ContentTitle";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import {
  __currentUser__,
  __jwtToken__,
  __projectKey__,
} from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface InternetLeadsProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const InternetLeadsConversion = ({
  setSelectedService,
  setSelectSubItem,
}: InternetLeadsProps) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isImportLoading, setIsImportLoading] = useState(false);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [internetLeadsData, setInternetLeadsData] = useState([]);
  const [importResultMsg, setImportResultMsg] = useState(undefined);
  const currentUser = useReactiveVar(__currentUser__);
  const jwtToken = useReactiveVar(__jwtToken__);
  const projectKey = useReactiveVar(__projectKey__);
  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 4,
      },
    });

  const props: UploadProps = {
    ...localUploadBaseProps,

    maxCount: 1,
    beforeUpload: (file) => {
      const isTxt = file.name.toLowerCase().endsWith('txt');

      if (!isTxt) {
        message.error(renderAntDMessageConfig("Only upload txt file!"));
      }

      return isTxt || Upload.LIST_IGNORE;
    }
  };

  const handleSubmit = () => {
    form.setFields([{ name: 'leadsNo', errors: [] }, { name: 'file', errors: [] }]);
    form
      .validateFields(['startDate', 'endDate'])
      .then(async () => {
        setIsLoading(true);
        const { startDate, endDate } = form.getFieldsValue();
        const header = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Project-Id": "AF",
            "X-Authorization-Provider": "Anonymous",
            "X-Project-Key": projectKey,
          },
          responseType: "blob",
        };
        const data = await apiCall({
          method: "GET",
          header: header,
          endPoint:
            process.env.REACT_APP_GET_INTERNET_LEADS_REPORT +
            dayjs(startDate).format("YYYY-MM-DD") +
            "/" +
            dayjs(endDate).format("YYYY-MM-DD"),
        });
        fileDownload(data, "InternetLeads.txt");
        setIsLoading(false);
      })
      .catch((e) => console.log(e));
  };

  const handleImportSubmit = () => {
    form.setFields([{ name: 'startDate', errors: [] }, { name: 'endDate', errors: [] }]);
    form
      .validateFields(['leadsNo', 'file'])
      .then(async () => {
        setIsImportLoading(true);
        const { leadsNo, file } = form.getFieldsValue();
        const formData = new FormData();
        formData.append("file", file[0]?.originFileObj);
        formData.append("leadsNo", leadsNo);
        formData.append("userEmail", currentUser?.userEmail || "");

        const header = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Project-Id": "AF",
            "X-Authorization-Provider": "Anonymous",
            "X-Project-Key": projectKey,
          },
        };

        var data = await apiCall({
          method: "POST",
          header: header,
          endPoint: process.env.REACT_APP_IMPORT_INTERNET_LEADS,
          payload: formData,
        });
        if ("success" in data) {
          if (data.success === true) {
            message.success("Import file successfully");
            setImportResultMsg(data.msg);
          } else if (data.success === false) {
            message.error(data.msg);
          }
        }
        else message.error("Import file failed");

        setIsImportLoading(false);
      })
      .catch((e) => console.log(e));
  };

  const handleQuery = () => {
    setIsQueryLoading(true);
    fetchInternetLeadsData(jwtToken, projectKey)
      .then((data) => {
        setInternetLeadsData(data);
      });
    setIsQueryLoading(false);
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.MKT);
    setSelectSubItem("Internet Leads Conversion");
  }, []);

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Internet Leads Conversion"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <>
      <div id="internetLeadsWrapper">
        <ContentTitle title="Internet Leads 轉檔" />
        <div className="table-container">
          <Form form={form} name="ealm-bonus-form" className="form">
            <div className="flex">
              <Form.Item
                name="startDate"
                rules={[requiredRule]}
                label="接收日期(起)"
                className="item"
              >
                <DatePicker className="w-full" />
              </Form.Item>
              <Form.Item
                name="endDate"
                rules={[requiredRule]}
                label="接收日期(迄)"
                className="ms-5 item"
              >
                <DatePicker className="w-full" />
              </Form.Item>
              <Button
                type="primary"
                className="ms-5"
                onClick={handleSubmit}
                loading={isLoading}
              >
                匯出文字檔
              </Button>
            </div>
            <Divider />
            <div className="flex">
              <Space align="start" size="middle">
                <Form.Item
                  name="leadsNo"
                  label="Leads No(起)"
                  className="item"
                  rules={[requiredRule,
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        const str = String(value);
                        if (/^\d{8}$/.test(str)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Leads No must be 8 digits.'));
                      },
                    },
                  ]}
                >
                  <InputNumber step={1} precision={0} className="w-full" />
                </Form.Item>
                <Form.Item
                  name="file"
                  rules={[requiredRule]}
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e.fileList}
                >
                  <Upload
                    {...props}
                  >
                    <Button icon={<UploadOutlined />} style={{ width: 200 }} >
                      上傳文字檔
                    </Button>
                  </Upload>
                </Form.Item>
              </Space>
              <Button
                type="primary"
                className="ms-5"
                onClick={handleImportSubmit}
                loading={isImportLoading}
              >
                匯入資料
              </Button>
            </div>
            {importResultMsg && (
              <p style={{ color: "red" }}>
                {importResultMsg}
              </p>
            )}
          </Form>
        </div>
      </div>
      <div className="theme-tab-content-container">
        <div
          className="theme-tab-table-container"
          style={{ backgroundColor: Colors.white }}
        >
          <div >
            <Button
              type="primary"
              className="item"
              onClick={handleQuery}
              loading={isQueryLoading}
            >
              查詢匯入資料
            </Button>
          </div>
          <Table
            columns={createColumns()}
            dataSource={internetLeadsData}
            pagination={false}
            scroll={{
              x: 300,
              y: 500,
            }}
            className="announcement-table"
          />
        </div>
      </div>
    </>
  );
};

export default InternetLeadsConversion;
